"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Wifi, RefreshCw, Database } from "lucide-react";

export default function QrDoorOfflineScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
                {t("Connection at the door", "Conexión en la puerta")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("Weak signal at the door? ", "¿Señal débil en la puerta? ")}
                <br />
                <span className="text-white/30">{t("Check it before doors.", "Revísala antes de abrir.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "The door screen validates every scan online, over WiFi or mobile data, and shows the connection clearly. If the signal drops, scanning pauses and resumes on its own when it's back. Test the signal at the entrance before doors open — use mobile data or a repeater if WiFi is weak there.",
                  "La pantalla de puerta valida cada escaneo en línea, con WiFi o datos móviles, y muestra la conexión con claridad. Si se cae la señal, el escaneo se pausa y vuelve solo cuando regresa. Prueba la señal en la entrada antes de abrir — usa datos móviles o un repetidor si el WiFi es débil ahí."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 rounded-full">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(46,204,113,0.15)" }}>
                  <Wifi className="w-3.5 h-3.5" style={{ color: "#2ECC71" }} />
                </div>
                <div className="text-[13px] font-bold text-white">
                  {t("Works over WiFi or mobile data", "Funciona con WiFi o datos móviles")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Door status mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4" style={{ color: "#C4B5FD" }} />
                  <div className="text-[10px] font-black tracking-widest uppercase text-white/55">
                    {t("Door status", "Estado de puerta")}
                  </div>
                </div>
                <div
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: "rgba(244,114,182,0.15)",
                    color: "#F472B6",
                  }}
                >
                  {t("Online", "En línea")}
                </div>
              </div>

              {/* Signal check */}
              <div className="mb-5">
                <div className="flex items-center justify-between text-[11px] mb-2">
                  <span className="font-bold text-white/65">
                    {t("Signal check at the entrance", "Prueba de señal en la entrada")}
                  </span>
                  <span className="font-bold" style={{ color: "#2ECC71" }}>{t("Good", "Buena")}</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: "0%" }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.5, delay: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
                    className="h-full rounded-full"
                    style={{ background: "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)" }}
                  />
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: t("Buyers", "Compradores"), value: "487" },
                  { label: t("Promoters", "Promotores"), value: "12" },
                  { label: t("Connection", "Conexión"), value: t("Mobile data", "Datos móviles") },
                ].map((s) => (
                  <div key={s.label} className="bg-white/[0.03] border border-white/5 rounded-[12px] p-3">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-white/45 mb-1">
                      {s.label}
                    </div>
                    <div className="text-[14px] font-black text-white tabular-nums">{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Signal drop simulation */}
              <div
                className="rounded-[14px] p-4"
                style={{
                  backgroundColor: "rgba(46,204,113,0.08)",
                  border: "1px solid rgba(46,204,113,0.25)",
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "rgba(46,204,113,0.25)" }}
                  >
                    <RefreshCw className="w-4 h-4" style={{ color: "#2ECC71" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-black text-white mb-1">
                      {t("Signal dropped at 11:14 PM", "Se cayó la señal a las 11:14 PM")}
                    </div>
                    <div className="text-[11px] text-white/55 leading-relaxed">
                      {t(
                        "The door screen showed it right away and scanning paused. Signal back at 11:15 PM — scanning resumed on its own.",
                        "La pantalla de puerta lo mostró al instante y el escaneo se pausó. La señal volvió a las 11:15 PM — el escaneo siguió solo."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
